// The queue's filter state, in the two shapes it has to exist in: the browser URL and
// the query string sent to GET /api/tickets (docs/API.md §3.1).
//
// They are close but not identical — the URL omits defaults so a clean queue has a
// clean address, while the API call is explicit about sort and limit — which is
// exactly why they are written once, here, rather than twice in the page component.
//
// Only documented params are ever emitted. An unrecognised one is
// `400 INVALID_QUERY_PARAM` on purpose (docs/API.md §1.6): a typo that quietly widens
// a support queue is worse than a failed request.

import { effectiveSort } from './sort-preference.js';

export const PAGE_SIZE = 25;
// What the server falls back to when nobody asked and no view decided — kept in step
// with SORT_DEFAULT in src/services/tickets.js. Documentation and a test fixture: this
// file deliberately NEVER sends it (see `readFilters`), because doing so would override
// every view's own order.
export const DEFAULT_SORT = '-created_at';

function parseCsv(value) {
  return (value ?? '').split(',').map((part) => part.trim()).filter(Boolean);
}

/** URLSearchParams → the shape the filter bar and both serialisers read. */
export function readFilters(searchParams) {
  const offset = Number.parseInt(searchParams.get('offset') ?? '0', 10);
  return {
    view: searchParams.get('view') ?? '',
    statuses: parseCsv(searchParams.get('status')),
    channels: parseCsv(searchParams.get('channel')),
    assignee: searchParams.get('assigned_user_id') ?? '',
    q: searchParams.get('q') ?? '',
    // '' means NOBODY ASKED — not "sort by the default". The distinction is
    // load-bearing now that views exist: a view carries its own sort, and the API
    // applies it only when the request names none. Defaulting this to
    // DEFAULT_SORT here (as it did before views) would have sent a sort on every
    // request and silently overridden every view's ordering, which looks exactly
    // like the feature not working.
    sort: searchParams.get('sort') ?? '',
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

function appendFilters(params, filters) {
  // A view NARROWS: the server ANDs it with whatever else is here rather than
  // replacing it, so a view and explicit filters compose (docs/API.md §3.1).
  if (filters.view) params.set('view', filters.view);
  if (filters.statuses.length) params.set('status', filters.statuses.join(','));
  if (filters.channels.length) params.set('channel', filters.channels.join(','));
  if (filters.assignee) params.set('assigned_user_id', filters.assignee);
  return params;
}

/** What goes in the address bar: shareable, and free of redundant defaults. */
export function toSearchParams(filters) {
  const params = appendFilters(new URLSearchParams(), filters);
  if (filters.q) params.set('q', filters.q);
  // Emitted whenever it is set, including when it equals DEFAULT_SORT. The old
  // `!== DEFAULT_SORT` test would drop an explicitly chosen sort from the URL when it
  // happened to equal the default — harmless before views, but now naming a sort is the
  // only way to say "order this view by that instead of by its own".
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.offset) params.set('offset', String(filters.offset));
  return params;
}

/** What goes to the API. */
export function toApiQuery(filters, preferredSort = '') {
  const params = appendFilters(new URLSearchParams(), filters);
  // A `q` shorter than two characters is rejected by the API, so it never leaves here;
  // the filter bar says why rather than letting a 400 explain it.
  const q = filters.q.trim();
  if (q.length >= 2) params.set('q', q);
  // Omitted when unset, so the view's sort survives. With no view the server falls back
  // to its own SORT_DEFAULT, which is `-created_at` — newest first. Named there and not
  // here on purpose: one default, in the file that applies it.
  //
  // `preferredSort` is the agent's own remembered order and is passed IN rather than read
  // here, so this stays a pure function of its arguments and the tests do not need a
  // localStorage. `effectiveSort` is what decides it can be sent at all: never while a
  // view is open, because a view carries its own order.
  const sort = effectiveSort({
    urlSort: filters.sort, viewId: filters.view, preference: preferredSort,
  });
  if (sort) params.set('sort', sort);
  params.set('limit', String(PAGE_SIZE));
  if (filters.offset) params.set('offset', String(filters.offset));
  return params.toString();
}
